import React from 'react';
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Chip,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { CustomColumnDefinition } from '../../types/lead';
import { useAutoPositionedMenu } from '../../hooks/ui/useAutoPositionedMenu';
import OptionMenuPaper from '../leads/cells/OptionMenuPaper';
import { resolveOptionLabel } from '../../utils/leads/leadsUtils';

interface DynamicFieldInputProps {
  column: CustomColumnDefinition;
  value: any;
  onChange: (value: any) => void;
  onAddOption?: (columnKey: string, newOptionLabel: string) => Promise<any>;
  error?: string;
  helperText?: string;
}

export const DynamicFieldInput: React.FC<DynamicFieldInputProps> = ({
  column,
  value,
  onChange,
  onAddOption,
  error,
  helperText,
}) => {
  const { name, type, options } = column;
  const { onOpen, MenuProps } = useAutoPositionedMenu({ desiredMaxHeight: 240 });

  const activeHelperText = error || helperText;

  const helperProps = { sx: { wordBreak: 'break-word', overflowWrap: 'anywhere' } };

  switch (type) {
    case 'phone':
      return (
        <MuiTelInput
          fullWidth
          defaultCountry="IN"
          preferredCountries={['IN', 'US', 'GB', 'CA', 'AU', 'AE', 'SG', 'NZ', 'ZA', 'DE', 'FR', 'IT', 'ES']}
          forceCallingCode
          disableFormatting
          label={name}
          value={value || ''}
          onChange={(newPhone) => onChange(newPhone)}
          error={!!error}
          FormHelperTextProps={helperProps}
          helperText={activeHelperText}
        />
      );

    case 'textarea':
      return (
        <TextField
          fullWidth
          multiline
          rows={3}
          label={name}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          FormHelperTextProps={helperProps}
          helperText={activeHelperText}
        />
      );

    case 'number':
      return (
        <TextField
          fullWidth
          type="number"
          label={name}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          error={!!error}
          FormHelperTextProps={helperProps}
          helperText={activeHelperText}
        />
      );

    case 'boolean':
      return (
        <FormControl fullWidth error={!!error}>
          <InputLabel>{name}</InputLabel>
          <Select
            value={value === true ? 'true' : value === false ? 'false' : ''}
            label={name}
            onChange={(e) => {
              const val = e.target.value;
              onChange(val === 'true' ? true : val === 'false' ? false : null);
            }}
            onOpen={onOpen}
            MenuProps={MenuProps}
          >
            <MenuItem value="true" sx={{ fontSize: '0.875rem', borderRadius: 1, mx: 0.75, my: 0.25, py: 0.75 }}>
              Yes
            </MenuItem>
            <MenuItem value="false" sx={{ fontSize: '0.875rem', borderRadius: 1, mx: 0.75, my: 0.25, py: 0.75 }}>
              No
            </MenuItem>
          </Select>
          {activeHelperText && <FormHelperText sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{activeHelperText}</FormHelperText>}
        </FormControl>
      );

    case 'select': {
      const selectOpts = options || [];
      const selectMenuProps = onAddOption
        ? {
            ...MenuProps,
            PaperProps: {
              ...(MenuProps?.PaperProps || {}),
              component: OptionMenuPaper,
              onAddOption: async (newOpt: string) => {
                await onAddOption(column.key, newOpt);
                const addedIdx = selectOpts.length;
                onChange(addedIdx);
                return addedIdx;
              },
            },
          }
        : MenuProps;

      let normalizedValue = value;
      if (Array.isArray(normalizedValue)) {
        normalizedValue = normalizedValue[0];
      } else if (typeof normalizedValue === 'string' && normalizedValue.trim().startsWith('[') && normalizedValue.trim().endsWith(']')) {
        try {
          const parsed = JSON.parse(normalizedValue);
          if (Array.isArray(parsed)) normalizedValue = parsed[0];
        } catch (e) {}
      }

      let selectedValueIndex = -1;
      if (typeof normalizedValue === 'number' && Number.isInteger(normalizedValue) && normalizedValue >= 0 && normalizedValue < selectOpts.length) {
        selectedValueIndex = normalizedValue;
      } else if (typeof normalizedValue === 'string') {
        const matchIdx = selectOpts.findIndex((opt) => opt === normalizedValue);
        if (matchIdx !== -1) {
          selectedValueIndex = matchIdx;
        } else {
          const numIdx = Number(normalizedValue);
          if (!isNaN(numIdx) && Number.isInteger(numIdx) && numIdx >= 0 && numIdx < selectOpts.length) {
            selectedValueIndex = numIdx;
          }
        }
      }

      return (
        <FormControl fullWidth error={!!error}>
          <InputLabel>{name}</InputLabel>
          <Select
            value={selectedValueIndex !== -1 ? selectedValueIndex : ''}
            label={name}
            onChange={(e) => onChange(Number(e.target.value))}
            onOpen={onOpen}
            MenuProps={selectMenuProps}
          >
            {selectOpts.map((optLabel, index) => (
              <MenuItem
                key={`${optLabel}-${index}`}
                value={index}
                sx={{
                  fontSize: '0.875rem',
                  borderRadius: 1,
                  mx: 0.75,
                  my: 0.25,
                  py: 0.75,
                  whiteSpace: 'nowrap',
                  '&.Mui-selected': {
                    bgcolor: 'primary.50',
                    fontWeight: 600,
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.100',
                    },
                  },
                }}
              >
                {optLabel}
              </MenuItem>
            ))}
          </Select>
          {activeHelperText && <FormHelperText sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{activeHelperText}</FormHelperText>}
        </FormControl>
      );
    }

    case 'multi_select': {
      const multiOpts = options || [];
      const multiSelectMenuProps = onAddOption
        ? {
            ...MenuProps,
            PaperProps: {
              ...(MenuProps?.PaperProps || {}),
              component: OptionMenuPaper,
              onAddOption: async (newOpt: string) => {
                await onAddOption(column.key, newOpt);
                const addedIdx = multiOpts.length;
                const currentArr = Array.isArray(value) ? value : [];
                if (!currentArr.includes(addedIdx)) {
                  onChange([...currentArr, addedIdx]);
                }
                return addedIdx;
              },
            },
          }
        : MenuProps;

      let rawArr: any[] = [];
      if (Array.isArray(value)) {
        rawArr = value;
      } else if (typeof value === 'string' && value.trim() !== '') {
        const trimmed = value.trim();
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) rawArr = parsed;
            else rawArr = [parsed];
          } catch (e) {
            rawArr = trimmed.split(',').map((s) => s.trim());
          }
        } else {
          rawArr = trimmed.split(',').map((s) => s.trim());
        }
      } else if (typeof value === 'number') {
        rawArr = [value];
      }

      const selectedIndices = rawArr
        .map((v) => {
          if (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < multiOpts.length) {
            return v;
          }
          if (typeof v === 'string') {
            const matchIdx = multiOpts.findIndex((o) => o === v);
            if (matchIdx !== -1) return matchIdx;
            const numIdx = Number(v);
            if (!isNaN(numIdx) && Number.isInteger(numIdx) && numIdx >= 0 && numIdx < multiOpts.length) {
              return numIdx;
            }
          }
          return -1;
        })
        .filter((idx) => idx >= 0 && idx < multiOpts.length);

      return (
        <FormControl fullWidth error={!!error}>
          <InputLabel>{name}</InputLabel>
          <Select
            multiple
            value={selectedIndices}
            onChange={(e) => {
              const valArr = e.target.value;
              const nextIndices = (typeof valArr === 'string' ? valArr.split(',') : valArr).map(Number);
              onChange(nextIndices);
            }}
            onOpen={onOpen}
            input={<OutlinedInput label={name} />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as number[]).map((idx) => (
                  <Chip
                    key={idx}
                    label={multiOpts[idx] || resolveOptionLabel(idx, multiOpts)}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderRadius: '5px',
                      bgcolor: '#f1f5f9',
                      border: '1px solid #cbd5e1',
                      color: 'text.primary',
                      maxWidth: '100%',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                ))}
              </Box>
            )}
            MenuProps={multiSelectMenuProps}
          >
            {multiOpts.map((optLabel, index) => {
              const isChecked = selectedIndices.includes(index);
              return (
                <MenuItem
                  key={`${optLabel}-${index}`}
                  value={index}
                  selected={isChecked}
                  sx={{
                    fontSize: '0.875rem',
                    borderRadius: 1,
                    mx: 0.75,
                    my: 0.25,
                    py: 0.75,
                    whiteSpace: 'nowrap',
                    '&.Mui-selected': {
                      bgcolor: 'primary.50',
                      fontWeight: 600,
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.100',
                      },
                    },
                  }}
                >
                  {optLabel}
                </MenuItem>
              );
            })}
          </Select>
          {activeHelperText && <FormHelperText sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{activeHelperText}</FormHelperText>}
        </FormControl>
      );
    }

    case 'date':
      return (
        <TextField
          fullWidth
          type="date"
          label={name}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={!!error}
          FormHelperTextProps={helperProps}
          helperText={activeHelperText}
        />
      );

    case 'time':
      return (
        <TextField
          fullWidth
          type="time"
          label={name}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={!!error}
          FormHelperTextProps={helperProps}
          helperText={activeHelperText}
        />
      );

    case 'datetime':
      return (
        <TextField
          fullWidth
          type="datetime-local"
          label={name}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={!!error}
          FormHelperTextProps={helperProps}
          helperText={activeHelperText}
        />
      );

    case 'email': {
      const trimmedVal = typeof value === 'string' ? value.trim() : '';
      const isInvalidEmail = Boolean(
        trimmedVal.length > 0 &&
          (!trimmedVal.includes('@') || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedVal))
      );
      const emailError =
        error ||
        (isInvalidEmail
          ? !trimmedVal.includes('@')
            ? "Please enter a valid email address"
            : 'Please enter a valid email address'
          : undefined);

      return (
        <TextField
          fullWidth
          type="email"
          label={name}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          error={!!emailError}
          FormHelperTextProps={helperProps}
          helperText={emailError || helperText}
        />
      );
    }

    case 'url': {
      const trimmedUrl = typeof value === 'string' ? value.trim() : '';
      const isInvalidUrl = Boolean(
        trimmedUrl.length > 0 &&
          !/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/.*)?$/i.test(trimmedUrl)
      );
      const urlError =
        error || (isInvalidUrl ? 'Please enter a valid URL' : undefined);

      return (
        <TextField
          fullWidth
          type="url"
          label={name}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          error={!!urlError}
          FormHelperTextProps={helperProps}
          helperText={urlError || helperText}
        />
      );
    }

    case 'text':
    default:
      return (
        <TextField
          fullWidth
          type="text"
          label={name}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          FormHelperTextProps={helperProps}
          helperText={activeHelperText}
        />
      );
  }
};
